/**
 * Le icone della barra.
 *
 * Disegnate qui invece di prese da una libreria: sono sei, pesano poche righe,
 * e una dipendenza in più andrebbe scaricata da qualche parte — che su questo
 * sito è la cosa che non vogliamo fare.
 *
 * Ognuna eredita il colore e prende la misura dalla classe, e porta sempre un
 * `aria-hidden`: l'etichetta la mette chi la usa sul pulsante, dove serve a chi
 * legge con la voce.
 */

type Props = { className?: string };

const comuni = {
  viewBox: "0 0 24 24",
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 1.8,
  strokeLinecap: "round" as const,
  strokeLinejoin: "round" as const,
  "aria-hidden": true,
};

export function IconaMenu({ className }: Props) {
  return (
    <svg {...comuni} className={className}>
      <path d="M3 6h18M3 12h18M3 18h18" />
    </svg>
  );
}

export function IconaImpostazioni({ className }: Props) {
  return (
    <svg {...comuni} className={className}>
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.7 1.7 0 0 0 .3 1.9l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.7 1.7 0 0 0-1.9-.3 1.7 1.7 0 0 0-1 1.5V21a2 2 0 1 1-4 0v-.1A1.7 1.7 0 0 0 9 19.4a1.7 1.7 0 0 0-1.9.3l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1a1.7 1.7 0 0 0 .3-1.9 1.7 1.7 0 0 0-1.5-1H3a2 2 0 1 1 0-4h.1A1.7 1.7 0 0 0 4.6 9a1.7 1.7 0 0 0-.3-1.9l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1a1.7 1.7 0 0 0 1.9.3H9a1.7 1.7 0 0 0 1-1.5V3a2 2 0 1 1 4 0v.1a1.7 1.7 0 0 0 1 1.5 1.7 1.7 0 0 0 1.9-.3l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.7 1.7 0 0 0-.3 1.9V9a1.7 1.7 0 0 0 1.5 1H21a2 2 0 1 1 0 4h-.1a1.7 1.7 0 0 0-1.5 1z" />
    </svg>
  );
}

export function IconaLingua({ className }: Props) {
  return (
    <svg {...comuni} className={className}>
      <circle cx="12" cy="12" r="9" />
      <path d="M3 12h18M12 3a15 15 0 0 1 0 18a15 15 0 0 1 0-18z" />
    </svg>
  );
}

export function IconaCarrello({ className }: Props) {
  return (
    <svg {...comuni} className={className}>
      <path d="M2 3h2.2l1 3m0 0 2 8h10l2.4-8H5.2z" />
      <circle cx="9" cy="20" r="1.4" />
      <circle cx="17" cy="20" r="1.4" />
    </svg>
  );
}

export function IconaPersona({ className }: Props) {
  return (
    <svg {...comuni} className={className}>
      <circle cx="12" cy="8" r="3.6" />
      <path d="M4.5 20a7.5 7.5 0 0 1 15 0" />
    </svg>
  );
}

export function IconaGraffetta({ className }: Props) {
  return (
    <svg {...comuni} className={className}>
      <path d="M21 11.5 12.5 20a5 5 0 0 1-7-7l8.8-8.8a3.3 3.3 0 1 1 4.7 4.7l-8.8 8.8a1.7 1.7 0 0 1-2.4-2.4l8.1-8.1" />
    </svg>
  );
}

export function IconaInvia({ className }: Props) {
  return (
    <svg {...comuni} className={className}>
      <path d="M12 19V5M5 12l7-7 7 7" />
    </svg>
  );
}

/**
 * Youppy, ridotto all'osso: due occhi e il sorriso.
 *
 * Il marchio intero non regge a 18 pixel — la scritta curva diventa una
 * macchia e il carrello un puntino. Quello che sopravvive alla rimpicciolita
 * e' la faccia, che e' anche la parte che si riconosce da lontano.
 *
 * Il sorriso e' una bezier e non un arco: con `A` bisogna ricordarsi che in
 * SVG la y cresce verso il basso e il verso dell'arco si ribalta, e un sorriso
 * al contrario e' un broncio.
 */
export function IconaYouppy({ className }: Props) {
  return (
    <svg viewBox="0 0 24 24" className={className} aria-hidden>
      <circle cx="8.6" cy="9" r="1.9" fill="currentColor" />
      <circle cx="15.4" cy="9" r="1.9" fill="currentColor" />
      <path
        d="M6.2 13.4Q12 19.6 17.8 13.4"
        fill="none"
        stroke="currentColor"
        strokeWidth={2}
        strokeLinecap="round"
      />
    </svg>
  );
}

/**
 * La freccia dei caroselli: solo il segno, senza cerchio intorno.
 *
 * E' la stessa forma della freccia dentro il pulsante di invio, ingrandita e
 * ingrossata. Senza pastiglia dietro deve reggersi da sola: da qui il tratto a
 * 3 invece di 1,8 — a parita' di dimensione un tratto sottile isolato sparisce
 * sopra una fotografia.
 *
 * **L'angolo al vertice e' 125 gradi, non 90.** Il chevron a 90 e' la punta di
 * un cursore, e a dimensioni grandi si legge come un segno di spunta storto;
 * aprendolo diventa una direzione. Le coordinate sono il vertice piu' due
 * bracci lunghi 8,6 inclinati di mezzo angolo: cambiare l'apertura vuol dire
 * ricalcolarle, non spostarle a occhio.
 *
 * Punta a sinistra. Quella a destra e' la stessa girata di mezzo giro, cosi'
 * le due restano identiche per costruzione invece che per somiglianza.
 */
export function IconaFreccia({ className }: Props) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={3}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden
    >
      <path d="M13.17 4.37 9.2 12l3.97 7.63" />
    </svg>
  );
}
